import { type ButtonHTMLAttributes, type FC, type ReactNode, forwardRef } from "react";
import { Spinner } from "./Spinner.tsx";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: "primary" | "secondary" | "outline" | "ghost" | "danger";
	size?: "sm" | "md" | "lg";
	loading?: boolean;
	asChild?: boolean;
	children: ReactNode;
}

const variantClasses: Record<string, string> = {
	primary:
		"bg-primary text-primary-fg hover:bg-primary-hover focus-visible:ring-primary disabled:opacity-50",
	secondary:
		"bg-surface-2 text-content hover:bg-line focus-visible:ring-primary disabled:opacity-50",
	outline:
		"border border-line bg-transparent text-content hover:bg-surface-2 focus-visible:ring-primary disabled:opacity-50",
	ghost:
		"bg-transparent text-content hover:bg-surface-2 focus-visible:ring-primary disabled:opacity-50",
	danger:
		"bg-danger text-primary-fg hover:bg-danger-hover focus-visible:ring-danger disabled:opacity-50",
};

const sizeClasses: Record<string, string> = {
	sm: "px-3 py-1.5 text-sm",
	md: "px-4 py-2 text-sm",
	lg: "px-6 py-3 text-base",
};

export const Button: FC<ButtonProps> = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = "primary",
			size = "md",
			loading = false,
			disabled,
			className = "",
			children,
			asChild,
			...props
		},
		ref,
	) => {
		const isDisabled = disabled || loading;

		return (
			<button
				ref={ref}
				disabled={isDisabled}
				className={`inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
				aria-busy={loading}
				{...props}
			>
				{loading && <Spinner size="sm" />}
				{children}
			</button>
		);
	},
);

Button.displayName = "Button";
